/**
 * Настроенный HTTP клиент для Medme Stats API на базе fetch
 *
 * ✅ Автоматически добавляет JWT токен из cookie к каждому запросу
 * ✅ Отмена запросов через AbortSignal
 * ✅ Типизация всех запросов и ответов
 */

import { getAppConfig, resolveAbsoluteUrl } from "@/app/config/app-config"
import { refreshAuthTokens, saveAuthenticationTokens } from "@/shared/lib/auth"

// Типы для body запроса
export type BodyType<BodyData> = BodyData

// Тип для ошибок (для совместимости с react-query и swr)
export type ErrorType<Error> = Error

// NOTE: Универсальный парсер тела ответа по заголовку content-type и responseType
const getBody = async <T>(response: Response | Request): Promise<T> => {
	const contentType = response.headers.get("content-type") ?? ""

	if (contentType.includes("application/json")) {
		return response.json() as Promise<T>
	}

	if (
		contentType.includes("application/pdf") ||
		contentType.includes("application/octet-stream") ||
		contentType.includes(
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		) ||
		contentType.includes("application/zip")
	) {
		return response.blob() as Promise<T>
	}

	return response.text() as Promise<T>
}

// NOTE: Пересборка базового URL, сохраняя pathname и query
const getUrl = (contextUrl: string): string => {
	const baseUrl = resolveAbsoluteUrl(getAppConfig().apiUrl)
	const requestURL = new URL(contextUrl, baseUrl)

	return requestURL.href
}

// NOTE: Сборка заголовков, установка JWT и корректного Content-Type
const getHeaders = (
	headers?: HeadersInit,
	body?: BodyInit | null,
): HeadersInit => {
	const result = new Headers(headers)

	const isFormData = typeof FormData !== "undefined" && body instanceof FormData
	const isBlob = typeof Blob !== "undefined" && body instanceof Blob
	const hasBody = body != null
	if (hasBody && !isFormData && !isBlob && !result.has("Content-Type")) {
		result.set("Content-Type", "application/json")
	}

	return result
}

/**
 * HTTP клиент для API запросов (mutator для Orval)
 * Принимает (url, options) как стандартный fetch
 */
export const customFetch = async <T>(
	url: string,
	options: RequestInit,
): Promise<T> => {
	try {
		const requestUrl = getUrl(url)

		const headers = getHeaders(options.headers, options.body ?? null)

		const requestInit: RequestInit = {
			...options,
			headers,
			credentials: "include",
		}

		const request = new Request(requestUrl, requestInit)

		const response = await fetch(request)

		// Ошибки HTTP
		if (!response.ok) {
			let data: unknown
			if (response.status === 401) {
				// Will try to refresh tokens. Backend will set new auth tokens in cookie.
				const tokens = await refreshAuthTokens()
				if (tokens) {
					saveAuthenticationTokens({
						token: tokens.access_token,
						refreshToken: tokens.refresh_token,
					})
				}
			}

			try {
				data = await getBody(response)
			} catch {
				data = null
			}
			const errorData = {
				data,
				status: response.status,
				message: response.statusText,
			}
			throw errorData
		}

		const data = await getBody<T>(response)

		return {
			status: response.status,
			data,
			headers: response.headers,
		} as T
	} catch (error: unknown) {
		// Suppress logs for AbortError
		const isAbortError =
			typeof error === "object" &&
			error !== null &&
			"name" in error &&
			error.name === "AbortError"
		if (!isAbortError) {
			console.error("Error fetching data:", error)
		}
		throw error
	}
}

export default customFetch
